import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { generateClient, get } from "aws-amplify/api"
import { MDBTable, MDBTableHead, MDBTableBody, MDBIcon, MDBCollapse, MDBBtn, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalBody,
    MDBModalTitle, MDBCard, MDBCardBody, MDBCardTitle, MDBCardText, MDBCardImage, MDBBadge,
    MDBInput, MDBAlert} from "mdb-react-ui-kit"
import { Link, useNavigate, useParams } from "react-router-dom"
import { getLocalizedText } from "../../../utils/localizedText"
import { captureError } from "../../../utils/sentryHelpers"
import { isVariantProduct } from "../../../utils/boothMap"

export default function SelectBooth({company}) {

    const client = generateClient()
    const { t, i18n } = useTranslation()

    const { companyid, eventId } = useParams()
    const navigate = useNavigate()

    const [event, setEvent] = useState({})
    const[products, setProducts] = useState([])
    const [stockReservations, setStockReservations] = useState([])
    const [usersStockReservations, setUsersStockReservations] = useState([])
    const[loading, setLoading] = useState(true)

    const [selectedProducts, setSelectedProducts] = useState([])
    const [productEquipmentCollapsed, setProductEquipmentCollapsed] = useState({})

    const [selectedProduct, setSelectedProduct] = useState(null)
    const [showProductDetails, setShowProductDetails] = useState(false)

    const [notice, setNotice] = useState(null)
    const [desiredBoothNumbers, setDesiredBoothNumbers] = useState([])
    const [hiddenVariantCount, setHiddenVariantCount] = useState(0)


    const handleViewProduct = (productId) => {
        setSelectedProduct(products.find(product => product.id === productId))
        setShowProductDetails(true)
    }


    const getEvent = async () => {
        try {
            const event = await client.graphql({
                query: /* GraphQL */ `
                query GetEvent($id: ID!) {
                  getEvent(id: $id) {
                    id
                    name {
                      language
                      text
                    }
                    bookingNotice {
                        language
                        text
                    }
                    startDate
                    endDate
                    image {
                        fileName
                        alt
                    }
                    boothPlan {
                        activeVersionId
                    }
                    eventProducts(limit: 1000) {
                        items {
                            id
                            price
                            stock
                            product {
                            id
                            name {
                                language
                                text
                                __typename
                            }
                            invoiceName {
                                language
                                text
                                __typename
                            }
                            description {
                                language
                                text
                                __typename
                            }
                            shortDescription {
                                language
                                text
                                __typename
                            }
                            includes {
                                language
                                text
                            }
                            sku
                            ean
                            type
                            boothNumber
                            boothVariant
                            image {
                                main {
                                    fileName
                                    alt
                                    __typename
                                }
                                __typename
                            }
                            features {
                                __typename
                            }
                            files {
                                url
                                s3Path
                                fileName
                                type
                                name
                                __typename
                            }
                            components {
                                items {
                                    qty
                                    order
                                    componentProduct {
                                        id
                                        name {
                                        language
                                            text
                                        }
                                    }
                                }
                            }
                            allowedBuyerGroups
                            requiredShipping
                            icon
                            createdAt
                            updatedAt
                            bundleBoothAvaiableAccessoriesId
                            bundleBoothIncludedAccessoriesId
                            __typename
                            } 
                        }
                    }
                  }
                }
              `,
                variables: {
                    id: eventId
                }
            })

            setEvent(event.data.getEvent);
            const allBoothProducts = event.data.getEvent.eventProducts.items.map(i => ({...i, ...i.product})).filter(product => (product.type === "event_booth_partner" || product.type === "event_booth" || product.type === "event_space"))
            // variant products (one product for many booths) can only be booked on the hall plan, where the booth number is chosen
            const boothProducts = allBoothProducts.filter(product => !isVariantProduct(product))
            setHiddenVariantCount(allBoothProducts.length - boothProducts.length)
            setProducts(boothProducts.sort((a, b) => a.name[0].text.localeCompare(b.name[0].text)))
            return {event: event.data.getEvent, products: boothProducts}
        } catch (error) {
            console.warn("error fetching event", error)
            captureError(error, {
                feature: 'event-management',
                operation: 'fetch-event-for-booth-selection',
                consoleMessage: 'error fetching event',
                context: {
                    eventId
                }
            });
        }
    }


    const getStockReservations = async () => {
        try {
            const stockReservations = await client.graphql({
                query: /* GraphQL */ `
                query ListStockReservations(
                  $filter: ModelStockReservationFilterInput
                  $limit: Int
                  $nextToken: String
                ) {
                  listStockReservations(
                    filter: $filter
                    limit: $limit
                    nextToken: $nextToken
                  ) {
                    items {
                      id
                      companyStockReservationsId
                      qty
                      eventProduct {
                        product {
                            id
                        }
                        }
                    }
                    nextToken
                    __typename
                  }
                }
              `
              ,
                variables: {
                    limit: 1000
                }
            })
            return stockReservations.data.listStockReservations.items
        } catch (error) {
            console.warn("error fetching stockReservations", error)
            captureError(error, {
                feature: 'event-management',
                operation: 'fetch-stock-reservations',
                consoleMessage: 'error fetching stockReservations'
            });
        }
    }
    
    const loadData = async () => {
        const { event, products} = await getEvent()
        // Cut-over (15.10.2026): events with an active hall plan are booked on the interactive hall plan only.
        // Every old entry point (shop, company events, bookmarks) lands here first and is forwarded.
        if (event?.boothPlan?.activeVersionId) {
            navigate(`/mycompany/${companyid}/select-booth-map/${eventId}`, { replace: true })
            return
        }
        // const products = await getProducts(event.id)
        const stockReservations = await getStockReservations()
        setEvent(event)
        const companyReservations = stockReservations.filter(stockReservation => stockReservation.companyStockReservationsId === companyid && stockReservation.eventProduct?.product)
        setUsersStockReservations(companyReservations);
        // one cart entry per product: a booth can carry several rows of the company (e.g. a legacy hold next to
        // the hall-plan lock row), and reservations of hidden variant products / other events have no product here
        const selectedEventProducts = []
        companyReservations.forEach(stockReservation => {
            const product = products.find(product => product.id === stockReservation.eventProduct.product.id)
            if (product && !selectedEventProducts.some(selected => selected.id === product.id)) selectedEventProducts.push(product)
        })
        setSelectedProducts(selectedEventProducts)
        
        setProducts(products)
        setStockReservations(stockReservations.filter(stockReservation => stockReservation.eventProduct))
        setLoading(false)
    }


    useEffect(() => {
        loadData()
    }, [])

    const parsePriceIntToMoney = (price) => {
        return price.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
    }

    const handleChangeDesiredBoothNumber = (e, productId) => {
        const value = e.target.value
        setDesiredBoothNumbers({...desiredBoothNumbers, [productId]: value})
    }

    // every booth product of the event is an offer (variant product, hidden here): nothing to list or check out,
    // so the empty tables and "To Checkout 0" are left out and only the hall-plan hint remains
    const onlyOffers = !loading && hiddenVariantCount > 0 && products.length === 0

  return (
<>
    <div className="container">
        <div className="row">
            <div className="col-12">
            {!onlyOffers && (
            <Link to={`/checkout?${selectedProducts.map((selectedProduct) => `productId=${selectedProduct?.id}`).join("&")}&companyId=${companyid}&eventId=${eventId}` + (notice ? `&notes=${notice}` : "")
                + (Object.values(desiredBoothNumbers).filter(Boolean).length > 0 ? `&notes=${Object.keys(desiredBoothNumbers).map((productId) => `Stand[${products.find(p => p.id === productId) ? getLocalizedText(products.find(p => p.id === productId).name) : productId}]:${desiredBoothNumbers[productId]}`).join("&")}` : "")
            } className="float-end">
            <MDBBtn className="float-end" outline color="success" >{t("To Checkout")}
                <MDBBadge className='ms-2' color='primary'>
                    {selectedProducts.length}
                </MDBBadge>
            </MDBBtn>
            </Link>
            )}
            <div className="float-start">
            {event?.name && event?.startDate && event?.endDate ? (
                <>
                <h1>{t("Select your booth for")} {getLocalizedText(event.name)} {t("from")} {new Date(event.startDate).toLocaleDateString("de-DE")} {t("to")} {new Date(event.endDate).toLocaleDateString("de-DE")}</h1>
                <p>
                    {getLocalizedText(event.bookingNotice)}
                </p>
                </>
            ) : (
                <h1>{t("Loading")}...</h1>
            )}
            </div>
               
            </div>



        </div>
        {(event?.boothPlan?.activeVersionId || hiddenVariantCount > 0) && (
            <div className="row">
                <div className="col-12">
                    <MDBAlert open color="info" className="mt-3">
                        <MDBIcon fas icon="map-marked-alt" className="me-2" />
                        {onlyOffers
                            ? t("The booths of this event are booked on the hall plan.")
                            : hiddenVariantCount > 0
                            ? (event?.boothPlan?.activeVersionId
                                ? t("Further booths of this event can only be booked on the hall plan, where you choose the exact booth.")
                                : t("Further booths of this event can only be booked on the hall plan, which is not open yet."))
                            : t("The booths of this event can also be booked on the interactive hall plan.")}
                        {/* link only to an active plan (the map page has nothing to book without one) */}
                        {event?.boothPlan?.activeVersionId && (
                            <Link to={`/mycompany/${companyid}/select-booth-map/${eventId}`} className="ms-2 fw-bold">
                                {t("Go to the hall plan")}
                            </Link>
                        )}
                    </MDBAlert>
                </div>
            </div>
        )}
        {!onlyOffers && (
        <div className="row">
            {loading ? (
                <div className="col-12">
                    <p>{t("Loading")}...</p>
                </div>
            ) : (
                <MDBTable>
                    <MDBTableHead>
                        <tr>
                            <th>{t("Booth variant")}</th>
                            <th>{t("Equipment")}</th>
                            <th>{t("Price")}</th>
                            <th>{t("Available")}</th>
                            <th>{t("Action")}</th>
                        </tr>
                    </MDBTableHead>
                    <MDBTableBody>
                        {products.filter(p => p.type !== "event_booth_partner").sort((a, b) => {
                            // Check if product 'a' is selected
                            const aSelected = selectedProducts.some(selectedProduct => selectedProduct.id === a.id);
                            // Check if product 'b' is selected
                            const bSelected = selectedProducts.some(selectedProduct => selectedProduct.id === b.id);

                            if (aSelected && !bSelected) {
                                return -1; // 'a' is selected, 'b' is not, 'a' should come first
                            } else if (!aSelected && bSelected) {
                                return 1; // 'b' is selected, 'a' is not, 'b' should come first
                            } else {
                                // If both are selected or both are unselected, sort alphabetically by name
                                return getLocalizedText(a.name).localeCompare(getLocalizedText(b.name), undefined, { numeric: true });
                            }
                        }).map((product) => (
                            <tr key={product.id} 
                            className={selectedProducts.find((selectedProduct) => selectedProduct.id === product.id) ? "border-success" : ""}
                            style={selectedProducts.find((selectedProduct) => selectedProduct.id === product.id) ? {borderWidth: "2px", borderColor: "rgba(var(--mdb-success-rgb) !important", backgroundColor: "#E8F5E9"} : {borderWidth: "1px"}}
                           >
                                <td>
                                <div className='d-flex align-items-center' onClick={
                                    () => handleViewProduct(product.id)
                                
                                }>
                                    {product.image?.main?.fileName ?
                                        <img
                                            src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/ProductImages/"+product.image.main.fileName}
                                            className=""
                                            style={{ height: "50px", width: "50px", objectFit: "contain" }}
                                            alt={product.image.alt}
                                        />
                                    :
                                        <MDBIcon className="" style={{ height: "50px", width: "50px", textAlign: "center", paddingTop: "12px", fontSize: "20px" }} fas icon={product.icon} />
                                                
                                    }
                                    <div className='ms-3'>
                                    <p className='mb-1'>{getLocalizedText(product.name)}</p>
                                    </div>
                                    </div>
                                    {usersStockReservations.find((stockReservation) => stockReservation.eventProduct.product.id === product.id) && (
                                        <>
                                        
                                        <MDBBadge color='danger' className="text-break" style={{position: "absolute", fontSize: "13px"}}>{t("This stand has been reserved for your company.")}</MDBBadge>
                                        <div className="my-3"></div>
                                        </>
                                    )}
                                </td>
                                <td>
                                  <MDBBtn color="link" onClick={() => setProductEquipmentCollapsed({...productEquipmentCollapsed, [product.id]: !productEquipmentCollapsed[product.id]})}>
                                    {t("Show equipment")}
                                    </MDBBtn>
                                    <MDBCollapse open={productEquipmentCollapsed[product.id]}>
                                        <ul>
                                            {product.components?.items.sort((a, b) => (a.order || 0) - (b.order || 0)).map((component) => (
                                                <li key={component.componentProduct.id}>{component.qty}x {getLocalizedText(component.componentProduct.name)}</li>
                                            ))}
                                            {product.includes?.filter(i => i.language === i18n.language).map((include) => (

                                                <li key={include.language}> {include.text.split("\n").map((text, index) => (
                                                    <><span key={index}>{text}</span>{index < include.text.split("\n").length - 1 && (<br/>)}</>
                                                ))
                                                }</li>
                                            ))}
                                        </ul>
                                    </MDBCollapse>
                                </td>
                                <td>{parsePriceIntToMoney(product.price)}</td>
                                <td>{product.stock - stockReservations.filter((stockReservation) => stockReservation.eventProduct.product.id === product.id).length}</td>
                                <td>
                                {selectedProducts.find((selectedProduct) => selectedProduct.id === product.id) ? 
                                <>
                                    <MDBInput className="mb-2" label={t("Desired booth number")} onChange={(e) => handleChangeDesiredBoothNumber(e, product.id)} />
                                    <MDBBtn color="danger"
                                        onClick={() => {
                                            setSelectedProducts(selectedProducts.filter((selectedProduct) => selectedProduct.id !== product.id))
                                        }}
                                    >
                                        {t("Remove from cart")}
                                    </MDBBtn>
                                </>
                                :
                                product.stock - stockReservations.filter((stockReservation) => stockReservation.eventProduct.product.id === product.id).length > 0 || usersStockReservations.find((stockReservation) => stockReservation.eventProduct.product.id === product.id) ?

                                <MDBBtn color="success"
                                    onClick={() => {
                                        setSelectedProducts([...selectedProducts, product])
                                    }}

                                >
                                    {t("Add to cart")}
                                </MDBBtn>
                                :
                                <MDBBtn color="grey" disabled>
                                    {t("Sold out")}
                                </MDBBtn>
                            }

                                    
                                   
                                </td>
                            </tr>
                        ))}
                    </MDBTableBody>
                </MDBTable>
            )}
            {loading ? (
                <div className="col-12">
                    <p>{t("Loading")}...</p>
                </div>
            ) : (
                <MDBTable>
                    <MDBTableHead>
                        <tr>
                            <th>{t("Booth variant")}</th>
                            <th>{t("Price")}</th>
                            <th>{t("Notice")}</th>
                            <th>{t("Action")}</th>
                        </tr>
                    </MDBTableHead>
                    <MDBTableBody>
                        {products.filter(p => p.type === "event_booth_partner").map((product) => (
                            <tr key={product.id} 
                            className={selectedProducts.find((selectedProduct) => selectedProduct.id === product.id) ? "border-success" : ""}
                            style={selectedProducts.find((selectedProduct) => selectedProduct.id === product.id) ? {borderWidth: "2px", borderColor: "rgba(var(--mdb-success-rgb) !important", backgroundColor: "#E8F5E9"} : {borderWidth: "1px"}}
                           >
                                <td>
                                <div className='d-flex align-items-center' onClick={
                                    () => handleViewProduct(product.id)
                                
                                }>
                                    {product.image?.main?.fileName ?
                                        <img
                                            src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/ProductImages/"+product.image.main.fileName}
                                            className=""
                                            style={{ height: "50px", width: "50px", objectFit: "contain" }}
                                            alt={product.image.alt}
                                        />
                                    :
                                        <MDBIcon className="" style={{ height: "50px", width: "50px", textAlign: "center", paddingTop: "12px", fontSize: "20px" }} fas icon={product.icon} />
                                                
                                    }
                                    <div className='ms-3'>
                                    <p className='mb-1'>{getLocalizedText(product.name)}</p>
                                    </div>
                                    </div>
                                    {usersStockReservations.find((stockReservation) => stockReservation.eventProduct.product.id === product.id) && (
                                        <>
                                        
                                        <MDBBadge color='danger' className="text-break" style={{position: "absolute", fontSize: "13px"}}>{t("This stand has been reserved for your company.")}</MDBBadge>
                                        <div className="my-3"></div>
                                        </>
                                    )}
                                </td>
                                
                                <td>{parsePriceIntToMoney(product.price)}</td>
                                <td>
                                    <MDBInput label={t("Notice")} onChange={(e) => setNotice(e.target.value)} />
                                </td>
                                
                                <td>
                                {selectedProducts.find((selectedProduct) => selectedProduct.id === product.id) ? 
                                    <MDBBtn color="danger"
                                            onClick={() => {
                                                setSelectedProducts(selectedProducts.filter((selectedProduct) => selectedProduct.id !== product.id))
                                            }}
                                        >
                                            {t("Remove from cart")}
                                        </MDBBtn>

                                        :
                                        product.stock - stockReservations.filter((stockReservation) => stockReservation.eventProduct.product.id === product.id).length > 0 || usersStockReservations.find((stockReservation) => stockReservation.eventProduct.product.id === product.id) ?

                                        <MDBBtn color="success"
                                            onClick={() => {
                                                setSelectedProducts([...selectedProducts, product])
                                            }}

                                        >
                                            {t("Add to cart")}
                                        </MDBBtn>
                                        :
                                        <MDBBtn color="grey" disabled>
                                            {t("Sold out")}
                                        </MDBBtn>
                                    }

                                    
                                   
                                </td>
                            </tr>
                        ))}
                    </MDBTableBody>
                </MDBTable>
            )}
        </div>
        )}
    </div>
    <MDBModal open={showProductDetails} onClose={() => setShowProductDetails(false)}>
    <MDBModalDialog scrollable size="xl">
        <MDBModalContent>
            <MDBModalHeader>
                <MDBModalTitle>{t("Product Details")}</MDBModalTitle>
            </MDBModalHeader>
            <MDBModalBody>
                <MDBCard>
                    <MDBCardBody>
                        <MDBCardTitle>{getLocalizedText(selectedProduct?.name)}</MDBCardTitle>
                        <MDBCardText>{getLocalizedText(selectedProduct?.description)}</MDBCardText>
                        <MDBCardImage src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/ProductImages/"+selectedProduct?.image?.main?.fileName} alt={selectedProduct?.image?.main?.alt} position='top' />
                        
                        <MDBCardText>{t("Equipment")}:</MDBCardText>
                        
                        <ul>
                        {selectedProduct?.components?.items.sort((a, b) => (a.order || 0) - (b.order || 0)).map((component, index) => (
                            <li key={index}>{component.qty}x {getLocalizedText(component.componentProduct.name)}</li>
                        ))}
                        {selectedProduct?.includes?.filter(i => i.language === i18n.language).map((include, index) => (
                            <li key={index}>{include?.text}</li>
                        ))}
                        </ul>
                       
                       
                    </MDBCardBody>
                </MDBCard>
            </MDBModalBody>
        </MDBModalContent>
    </MDBModalDialog>
</MDBModal>
</>
  )
}
